/**
 * The goal creation/edit form — UX/UI Specification §9.9 / §14.
 *
 * Shared by the student profile's Hedefler tab and the report wizard's
 * Hedefler step, same reasoning as AssessmentForm: an instructor who
 * realises a goal is missing while building a report should be able to add
 * it there, not have to leave the wizard first.
 *
 * `records` is the student's full, unbounded measurement history — not a
 * report period's slice of it. "Güncel" reads the latest measurement
 * regardless of which screen opened this form (Doc 6 §14.2), the same way
 * for a fixed metric and for one of the studio's own custom fields.
 */

import { useState } from 'react';
import {
  customMetricSeries,
  customMetricsOf,
  formatValue,
  GOAL_TYPE_LABELS,
  GOAL_TYPE_METRIC,
  goalTracksMeasurement,
  latestValue,
  LIMITS,
  parseTypedNumber,
  QUALITATIVE_NOTE_MAX_LENGTH,
  type GoalType,
  type MeasurementRecord,
} from '@pp/shared';
import { ApiError, api } from '../api/client.js';
import { messageFor } from '../api/error-messages.js';
import { NumberField, numberFieldError } from './ui/NumberField.js';
import { Button } from './ui/Button.js';
import './GoalForm.css';

export interface GoalFormValue {
  id: string;
  goalType: GoalType;
  customMetricLabel: string | null;
  title: string;
  goalMode: 'numeric' | 'qualitative';
  startValue: number | null;
  currentValue: number | null;
  targetValue: number | null;
  unit: string | null;
  targetDate: string | null;
  qualitativeNote: string | null;
}

/** The prefix a <select> option carries for one of the student's own custom
 *  measurement fields, so one control can offer both kinds of goal. */
const CUSTOM_METRIC_PREFIX = 'custom_metric:';

/** Numeric types come with their unit already known. */
const NUMERIC_TYPES: Partial<Record<GoalType, string>> = {
  weight: 'kg',
  waist: 'cm',
  hip: 'cm',
  chest: 'cm',
  right_arm: 'cm',
  left_arm: 'cm',
  right_thigh: 'cm',
  left_thigh: 'cm',
  body_fat: '%',
  muscle: '%',
};

// 'custom_metric' is not a static option: it is offered per student, once for
// each of their own measurement fields, built from customMetricsOf below.
const ALL_TYPES = (Object.keys(GOAL_TYPE_LABELS) as GoalType[]).filter(
  (type) => type !== 'custom_metric',
);

/** Empty and unparseable both mean "no value" once the field has vetted it. */
function parseNumber(raw: string): number | null {
  const value = parseTypedNumber(raw);
  return typeof value === 'number' ? value : null;
}

function toInput(value: number | null): string {
  return value === null ? '' : String(value).replace('.', ',');
}

interface FormState {
  goalType: GoalType;
  /** The folded label of the selected custom metric, when goalType is
   *  'custom_metric'. Empty otherwise. */
  customMetricLabel: string;
  title: string;
  startValue: string;
  currentValue: string;
  targetValue: string;
  targetDate: string;
  note: string;
}

function formOf(initial: GoalFormValue | null): FormState {
  if (!initial) {
    return {
      goalType: 'waist',
      customMetricLabel: '',
      title: '',
      startValue: '',
      currentValue: '',
      targetValue: '',
      targetDate: '',
      note: '',
    };
  }
  return {
    goalType: initial.goalType,
    customMetricLabel: initial.customMetricLabel ?? '',
    title: initial.title,
    startValue: toInput(initial.startValue),
    currentValue: toInput(initial.currentValue),
    targetValue: toInput(initial.targetValue),
    targetDate: initial.targetDate ?? '',
    note: initial.qualitativeNote ?? '',
  };
}

export function GoalForm({
  studentId,
  records,
  initial = null,
  onSaved,
  onCancel,
}: {
  studentId: string;
  /** The student's full measurement history, fixed and custom. */
  records: MeasurementRecord[];
  /** Present when editing; absent when adding. */
  initial?: GoalFormValue | null;
  onSaved: (goal: GoalFormValue) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<FormState>(() => formOf(initial));
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string>();

  const customMetrics = customMetricsOf(records);
  const selectedCustomMetric =
    form.goalType === 'custom_metric'
      ? customMetrics.find((m) => m.labelNormalized === form.customMetricLabel)
      : undefined;

  const unit =
    form.goalType === 'custom_metric'
      ? selectedCustomMetric?.unit
      : NUMERIC_TYPES[form.goalType];
  const isNumeric = unit !== undefined;

  /*
   * A goal on weight, waist, hip, body fat or muscle — or one of the
   * student's own custom fields — reads its current value from the last
   * measurement, so the form does not ask for it. Typing it here was how a
   * report ended up showing 82 cm in the table and 87 cm on the goal card in
   * the same document (Doc 6 §14.2).
   *
   * When the metric has never been measured the field stays editable: hiding
   * it would leave the goal with no current value and no way to give it one,
   * and the server already treats the stored number as the fallback. The first
   * measurement takes over from it.
   */
  const trackedMetric = GOAL_TYPE_METRIC[form.goalType];
  const measured =
    form.goalType === 'custom_metric'
      ? form.customMetricLabel
        ? (customMetricSeries(records, form.customMetricLabel).at(-1)?.value ?? null)
        : null
      : trackedMetric
        ? (latestValue(records, trackedMetric)?.value ?? null)
        : null;
  const tracked = measured !== null;

  // A value the server would reject should not travel; the field already says
  // which one is wrong, so saving simply waits for it.
  const goalOutOfRange =
    isNumeric &&
    [form.startValue, ...(tracked ? [] : [form.currentValue]), form.targetValue].some(
      (raw) => numberFieldError(raw, LIMITS.goalValue) !== null,
    );

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setFormError(undefined);

    const fallbackTitle =
      form.goalType === 'custom_metric'
        ? (selectedCustomMetric?.label ?? GOAL_TYPE_LABELS.custom_metric)
        : GOAL_TYPE_LABELS[form.goalType];

    const body: Omit<GoalFormValue, 'id'> = {
      goalType: form.goalType,
      customMetricLabel:
        form.goalType === 'custom_metric' ? form.customMetricLabel : null,
      title: form.title.trim() || fallbackTitle,
      goalMode: isNumeric ? 'numeric' : 'qualitative',
      startValue: isNumeric ? parseNumber(form.startValue) : null,
      // Still sent for a tracked goal: it is the fallback the server keeps for
      // a metric that has not been measured yet.
      currentValue: isNumeric ? parseNumber(form.currentValue) : null,
      targetValue: isNumeric ? parseNumber(form.targetValue) : null,
      unit: isNumeric ? (unit ?? null) : null,
      targetDate: form.targetDate || null,
      qualitativeNote: form.note.trim() || null,
    };

    try {
      const base = `/api/students/${studentId}/goals`;
      if (initial) {
        await api.patch(`${base}/${initial.id}`, body);
        onSaved({ id: initial.id, ...body });
      } else {
        const created = await api.post<{ id: string }>(base, body);
        onSaved({ id: created.id, ...body });
      }
    } catch (err) {
      setFormError(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="goal-form" noValidate>
      <label className="goal-form__field">
        <span>Hedef Türü</span>
        <select
          value={
            form.goalType === 'custom_metric'
              ? `${CUSTOM_METRIC_PREFIX}${form.customMetricLabel}`
              : form.goalType
          }
          onChange={(e) => {
            const raw = e.target.value;
            if (raw.startsWith(CUSTOM_METRIC_PREFIX)) {
              setForm({
                ...form,
                goalType: 'custom_metric',
                customMetricLabel: raw.slice(CUSTOM_METRIC_PREFIX.length),
              });
            } else {
              setForm({ ...form, goalType: raw as GoalType, customMetricLabel: '' });
            }
          }}
        >
          {ALL_TYPES.map((type) => (
            <option key={type} value={type}>
              {GOAL_TYPE_LABELS[type]}
              {NUMERIC_TYPES[type] ? ` (${NUMERIC_TYPES[type]})` : ''}
            </option>
          ))}
          {/* The student's own measurement fields — "Bilek", "Boyun" — not
              named by the product, so they cannot be static options. */}
          {customMetrics.length > 0 && (
            <optgroup label="Özel Ölçümler">
              {customMetrics.map((metric) => (
                <option
                  key={metric.labelNormalized}
                  value={`${CUSTOM_METRIC_PREFIX}${metric.labelNormalized}`}
                >
                  {metric.label} ({metric.unit})
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </label>

      <label className="goal-form__field">
        <span>
          Başlık <em>(boş bırakılırsa tür adı kullanılır)</em>
        </span>
        <input
          type="text"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder={
            form.goalType === 'custom_metric'
              ? (selectedCustomMetric?.label ?? GOAL_TYPE_LABELS.custom_metric)
              : GOAL_TYPE_LABELS[form.goalType]
          }
        />
      </label>

      {isNumeric && (
        <div className="goal-form__row">
          <NumberField
            className="goal-form__field"
            label={`Başlangıç (${unit})`}
            limit={LIMITS.goalValue}
            value={form.startValue}
            onChange={(raw) => setForm({ ...form, startValue: raw })}
            required
          />
          {tracked ? (
            <div className="goal-form__field goal-form__tracked">
              <span className="goal-form__tracked-label">Güncel</span>
              <span className="goal-form__tracked-value">
                {formatValue(measured, unit ?? '', 1)}
              </span>
              <span className="goal-form__tracked-note">Son ölçümden alınır</span>
            </div>
          ) : (
            <NumberField
              className="goal-form__field"
              label={`Güncel (${unit})`}
              hint={
                goalTracksMeasurement(form.goalType)
                  ? 'İlk ölçüm girildiğinde buradan otomatik alınacak'
                  : undefined
              }
              limit={LIMITS.goalValue}
              value={form.currentValue}
              onChange={(raw) => setForm({ ...form, currentValue: raw })}
            />
          )}
          <NumberField
            className="goal-form__field"
            label={`Hedef (${unit})`}
            limit={LIMITS.goalValue}
            value={form.targetValue}
            onChange={(raw) => setForm({ ...form, targetValue: raw })}
            required
          />
        </div>
      )}

      <label className="goal-form__field">
        <span>
          Not{' '}
          <em>
            {isNumeric
              ? '(opsiyonel)'
              : '(bu hedef sayıyla ölçülmüyor — durumu buraya yazın)'}
          </em>
        </span>
        <textarea
          rows={2}
          maxLength={QUALITATIVE_NOTE_MAX_LENGTH}
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          placeholder={
            isNumeric ? '' : 'Alt vücut esnekliğinde belirgin artış gözlemledim.'
          }
        />
      </label>

      <label className="goal-form__field goal-form__field--narrow">
        <span>
          Hedef Tarih <em>(opsiyonel)</em>
        </span>
        <input
          type="date"
          value={form.targetDate}
          onChange={(e) => setForm({ ...form, targetDate: e.target.value })}
        />
      </label>

      {formError && (
        <p className="goal-form__error" role="alert">
          {formError}
        </p>
      )}

      <div className="goal-form__actions">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Vazgeç
        </Button>
        <Button type="submit" loading={busy} disabled={goalOutOfRange}>
          {initial ? 'Değişiklikleri Kaydet' : 'Kaydet'}
        </Button>
      </div>
    </form>
  );
}
